import { AlertTriangle, History, RefreshCw } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MerchantUser } from '../contexts/MerchantAuthContext';
import type { MerchantPaymentListItem } from '../lib/api';
import {
    merchantAPI
} from '../lib/api';
import { cabinetPath } from '../lib/domains';
import { merchantEvents, MERCHANT_EVENTS } from '../lib/merchantEvents';
import { AppealModal } from './AppealModal';
import { MerchantPaymentAmounts } from './MerchantPaymentAmounts';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';

type Payment = MerchantPaymentListItem;

interface PaymentHistorySectionProps {
    user: MerchantUser | null;
}

export const MerchantRecentPayments: React.FC<PaymentHistorySectionProps> = ({ user }) => {
    const navigate = useNavigate();
    const requestId = useRef(0);
    const [payments, setPayments] = useState<Payment[]>([]);
    const [loadingPayments, setLoadingPayments] = useState(false);
    const [paymentsError, setPaymentsError] = useState<string | null>(null);
    const [appealModalOpen, setAppealModalOpen] = useState(false);
    const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);

    const handleOpenAppeal = (payment: Payment) => {
        setSelectedPayment(payment);
        setAppealModalOpen(true);
    };

    const isPaymentTooOld = (payment: Payment): boolean => {
        if (!payment.created_at) return false;
        const transactionDate = new Date(payment.created_at);
        const daysSinceCreation = (Date.now() - transactionDate.getTime()) / (1000 * 60 * 60 * 24);
        return daysSinceCreation > 14;
    };

    const loadPayments = useCallback(async (showLoading = true) => {
        if (!user) return;

        const currentRequest = ++requestId.current;
        try {
            if (showLoading) {
                setLoadingPayments(true);
            }
            setPaymentsError(null);
            const response = await merchantAPI.getPayments(1, 10);

            if (currentRequest !== requestId.current) return;
            // Обновляем только если данные изменились
            setPayments(prevPayments => {
                const newPayments = [...(response.payments || [])]
                    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()).slice(0, 10);
                const dataString = JSON.stringify(newPayments);
                const prevString = JSON.stringify(prevPayments);

                if (dataString === prevString) {
                    return prevPayments;
                }

                return newPayments;
            });

        } catch (err: any) {
            if (currentRequest !== requestId.current) return;
            setPaymentsError('Не удалось загрузить историю платежей');
            console.error('Ошибка загрузки платежей:', err);
        } finally {
            if (currentRequest === requestId.current) {
                setLoadingPayments(false);
            }
        }
    }, [user]);

    useEffect(() => {
        if (!user) return;
        loadPayments();
        const refresh = () => { void loadPayments(false); };
        const interval = setInterval(refresh, 30000);
        merchantEvents.on(MERCHANT_EVENTS.PAYMENT_CREATED, refresh);
        merchantEvents.on(MERCHANT_EVENTS.PAYMENT_STATUS_CHANGED, refresh);
        return () => {
            requestId.current += 1;
            clearInterval(interval);
            merchantEvents.off(MERCHANT_EVENTS.PAYMENT_CREATED, refresh);
            merchantEvents.off(MERCHANT_EVENTS.PAYMENT_STATUS_CHANGED, refresh);
        };
    }, [user, loadPayments]);

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'assigned':
                return <Badge variant="secondary">Назначен</Badge>;
            case 'pending_requisites':
                return <Badge variant="outline" className="text-yellow-700 dark:text-yellow-300 border-yellow-400 bg-yellow-50 dark:bg-yellow-950">Ожидает реквизитов</Badge>;
            case 'receipt_uploaded':
                return <Badge variant="default">Чек загружен</Badge>;
            case 'confirmed':
                return <Badge className="bg-blue-600">Подтвержден</Badge>;
            case 'completed':
                return <Badge className="bg-green-600">Завершен</Badge>;
            case 'cancelled':
                return <Badge variant="outline" className="text-red-600 dark:text-red-300">Отменен</Badge>;
            case 'disputed':
                return <Badge variant="outline" className="border-orange-300 bg-orange-50 text-orange-700 dark:border-orange-800 dark:bg-orange-950 dark:text-orange-300">Спор</Badge>;
            case 'no_traders':
                return <Badge variant="outline" className="text-gray-500 dark:text-slate-400 border-gray-300 dark:border-slate-700 bg-gray-50 dark:bg-slate-900">Нет трейдеров</Badge>;
            default:
                return <Badge variant="outline">{status}</Badge>;
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('ru-RU', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const getOrderTypeIcon = (orderType: string) => {
        return orderType === 'deposit' ? '📥' : '📤';
    };

    const getOrderTypeLabel = (orderType: string) => {
        return orderType === 'deposit' ? 'Пополнение' : 'Вывод';
    };

    return (
        <>
            <Card>
                <CardHeader>
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="flex flex-wrap items-center gap-2">
                            <History className="h-5 w-5 text-green-600 dark:text-green-300" />
                            <CardTitle>Последние 10 платежей</CardTitle>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                            <Button variant="outline" size="sm" onClick={() => navigate(cabinetPath('merchant', '/payments-history'))}>Вся история</Button>
                            <Button
                                aria-label="Обновить последние платежи"
                                variant="outline"
                                size="sm"
                                onClick={() => loadPayments(true)}
                                disabled={loadingPayments}
                            >
                                {loadingPayments ? (
                                    <RefreshCw className="h-4 w-4 animate-spin" />
                                ) : (
                                    <RefreshCw className="h-4 w-4" />
                                )}
                            </Button>
                        </div>
                    </div>
                    <CardDescription>
                        Последние платежи через ваш API
                    </CardDescription>

                </CardHeader>
                <CardContent>
                    {paymentsError && (
                        <div className="p-3 text-sm text-red-500 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-md mb-4">
                            {paymentsError}
                        </div>
                    )}

                    {loadingPayments ? (
                        <div className="text-center py-8">
                            <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2 text-gray-400" />
                            <p className="text-gray-500 dark:text-slate-400">Загружаем платежи...</p>
                        </div>
                    ) : payments.length === 0 ? (
                        <div className="text-center py-8 text-gray-500 dark:text-slate-400">
                            <History className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                            <p>Платежей пока нет</p>
                            <p className="text-sm mt-2">Создайте свой первый платёж через API, чтобы начать</p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {payments.map((payment) => {
                                return (
                                    <div
                                        data-payment-id={payment.transaction_id}
                                        key={payment.transaction_id}
                                        className="flex flex-wrap items-center justify-between gap-3 p-4 border rounded-md hover:bg-gray-50 dark:hover:bg-slate-900 group"
                                    >
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-3">
                                                <div className="text-2xl">
                                                    {getOrderTypeIcon(payment.order_type)}
                                                </div>
                                                <div>
                                                    <div className="flex flex-wrap items-center gap-2">
                                                        <span className="break-all font-medium">
                                                            {payment.payment_id}
                                                        </span>
                                                        <Badge variant="outline" className="text-xs">
                                                            {getOrderTypeLabel(payment.order_type)}
                                                        </Badge>
                                                        {getStatusBadge(payment.status)}
                                                    </div>
                                                    <div className="text-sm text-gray-500 dark:text-slate-400">
                                                        {formatDate(payment.created_at)}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <div className="text-right">
                                                <MerchantPaymentAmounts payment={payment} />
                                                <div className="break-all text-xs text-gray-500 dark:text-slate-400">
                                                    ID: {payment.transaction_id}
                                                </div>
                                            </div>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleOpenAppeal(payment)}
                                                disabled={isPaymentTooOld(payment)}
                                                className={`transition-colors ${isPaymentTooOld(payment)
                                                    ? 'text-gray-400 cursor-not-allowed'
                                                    : 'text-orange-600 dark:text-orange-300 hover:text-orange-700 dark:hover:text-orange-300 hover:bg-orange-50 dark:hover:bg-orange-950'
                                                    }`}
                                                title={isPaymentTooOld(payment) ? 'Невозможно открыть спор: транзакция старше 14 дней' : 'Открыть спор'}
                                            >
                                                <AlertTriangle className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </CardContent>
            </Card>

            <AppealModal
                open={appealModalOpen}
                onOpenChange={setAppealModalOpen}
                payment={selectedPayment}
                apiKey={user?.api_key || ''}
            />
        </>
    );
};

